import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useUser, useAuth } from '@clerk/clerk-react';
import { colors } from './theme';
import LoadingSpinner from './components/LoadingSpinner';
import { useProductDetail, saveProduct } from './hooks/useProductMeta';
import { userCanEditProducts } from './roles';
import { canUseChangeControl } from './pages/quality/changes/changeControl';
import { fieldVisible, generateProductName, NAME_INPUT_FIELDS, NAME_GENERATOR_TYPES, ASPECT_RATIO_OPTIONS, MOUNTING_OPTIONS } from './utils/productFields';

const API_BASE = process.env.REACT_APP_API_URL;

const LIFECYCLE_STATUSES = [
  ['in_development', 'In development'], ['released', 'Released'], ['production', 'Production'],
  ['end_of_life', 'End of life'], ['void', 'Void'],
];

const TEXT_FIELDS = [
  ['family', 'Family'], ['size', 'Size'],
  ['resolution', 'Resolution'], ['ipRating', 'IP rating'],
  ['operatingVoltage', 'Operating voltage'],
  ['drawingReference', 'Drawing reference'], ['linkedDiu', 'Linked DIU P/N'],
  ['linkedElu', 'Linked ELU P/N'], ['parentMon', 'Parent MON P/N'],
  ['customerModelName', 'Customer model name'],
  ['customerInternalPN', 'Customer internal P/N'], ['datasheetUrl', 'Datasheet URL'],
  ['serialNumberLayout', 'Serial number layout'], ['ninApproval', 'NIN approval'],
  ['transmittalRef', 'Transmittal ref'],
];

// Known manufacturing suppliers (canonical key → label). NI assigns these; a
// supplier user only sees the products carrying their key.
const SUPPLIERS = [
  { key: 'gpv', label: 'GPV' },
  { key: 'ubiqconn', label: 'Ubiqconn' },
  { key: 'acmepoint', label: 'Acmepoint' },
  { key: 'hk_ringer', label: 'HK Ringer' },
];
const supplierLabel = (key) => (SUPPLIERS.find(s => s.key === key) || {}).label || key;

export default function ProductMetaPage() {
  const { productNumber } = useParams();
  const decoded = decodeURIComponent(productNumber);
  const navigate = useNavigate();
  const { user, isSignedIn } = useUser();
  const { getToken } = useAuth();
  const { item, isLoading, error, setItem } = useProductDetail(decoded);

  const canEdit = userCanEditProducts(user);

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  // While on, the name regenerates from the structured variables on every
  // change; typing in the name field switches it off (issue #707).
  const [nameAuto, setNameAuto] = useState(false);
  const [boms, setBoms] = useState([]);

  useEffect(() => {
    if (item) setForm({
      ...item,
      specs: Array.isArray(item.specs) ? item.specs.map(s => ({ ...s })) : [],
      certificationsText: Array.isArray(item.certifications) ? item.certifications.join(', ') : '',
    });
  }, [item]);

  // Released BOMs for the link picker, fetched once per edit session.
  useEffect(() => {
    if (!editing || !canEdit) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API_BASE}/quality/boms`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (!cancelled) setBoms((data.boms || []).filter(b => b.currentStatus === 'Released'));
      } catch {
        if (!cancelled) setBoms([]);
      }
    })();
    return () => { cancelled = true; };
  }, [editing, canEdit, getToken]);

  if (!isSignedIn) {
    return <div className="ni-page"><div className="ni-shell ni-shell--narrow"><div className="ni-panel ni-empty">Please sign in.</div></div></div>;
  }
  if (isLoading) {
    return <div className="ni-page"><div className="ni-shell ni-shell--narrow"><div className="ni-panel" style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><LoadingSpinner size={48} /></div></div></div>;
  }
  if (error || !item || !form) {
    return <div className="ni-page"><div className="ni-shell ni-shell--narrow"><div className="ni-panel ni-empty">Product “{decoded}” not found.</div></div></div>;
  }

  const setField = (k, v) => {
    if (k === 'name') setNameAuto(false);
    setForm(prev => {
      const next = { ...prev, [k]: v };
      if (k !== 'name' && nameAuto && NAME_INPUT_FIELDS.includes(k)) {
        const gen = generateProductName(next);
        if (gen) next.name = gen;
      }
      return next;
    });
  };

  const applyGeneratedName = () => {
    const gen = generateProductName(form);
    if (!gen) { setSaveError('Fill in the structured variables (size, power…) first — nothing to generate from yet.'); return; }
    setSaveError('');
    setNameAuto(true);
    setForm(prev => ({ ...prev, name: gen }));
  };

  const toggleMounting = (opt) => setForm(prev => {
    const cur = Array.isArray(prev.mounting) ? prev.mounting : [];
    return { ...prev, mounting: cur.includes(opt) ? cur.filter(m => m !== opt) : [...cur, opt] };
  });
  const setSpec = (i, k, v) => setForm(prev => {
    const specs = prev.specs.map((s, idx) => (idx === i ? { ...s, [k]: v } : s));
    return { ...prev, specs };
  });
  const addSpec = () => setForm(prev => ({ ...prev, specs: [...prev.specs, { key: '', value: '' }] }));
  const removeSpec = i => setForm(prev => ({ ...prev, specs: prev.specs.filter((_, idx) => idx !== i) }));
  const toggleSupplier = (key) => setForm(prev => {
    const cur = Array.isArray(prev.suppliers) ? prev.suppliers : [];
    return { ...prev, suppliers: cur.includes(key) ? cur.filter(k => k !== key) : [...cur, key] };
  });

  const cancel = () => {
    setForm({
      ...item,
      specs: (item.specs || []).map(s => ({ ...s })),
      certificationsText: Array.isArray(item.certifications) ? item.certifications.join(', ') : '',
    });
    setEditing(false); setSaveError(''); setNameAuto(false);
  };

  const save = async () => {
    const nextPn = (form.productNumber || '').trim();
    if (!nextPn) { setSaveError('Product number is required.'); return; }
    setSaving(true);
    setSaveError('');
    try {
      const payload = {
        productNumber: nextPn,
        type: form.type, name: form.name, family: form.family, size: form.size, status: form.status,
        touch: form.touch, biosReady: form.biosReady, datasheetConfirmed: form.datasheetConfirmed,
        powerSupply: form.powerSupply || '', bonding: form.bonding || '', brightness: form.brightness || '', interfaces: form.interfaces || '',
        nvis: form.nvis, aspectRatio: form.aspectRatio || '', resolution: form.resolution || '',
        ipRating: form.ipRating || '', operatingVoltage: form.operatingVoltage || '',
        customerModelName: form.customerModelName || '',
        // Only rebuild from the comma-separated text when it was actually edited —
        // an entry that itself contains a comma must survive a no-op save.
        certifications: (form.certificationsText || '') === (Array.isArray(item.certifications) ? item.certifications.join(', ') : '')
          ? (item.certifications || [])
          : (form.certificationsText || '').split(',').map(s => s.trim()).filter(Boolean),
        mounting: Array.isArray(form.mounting) ? form.mounting : [],
        bomId: form.bomId || '', bomDocumentNumber: form.bomDocumentNumber || '', bomName: form.bomName || '',
        linkedDiu: form.linkedDiu, linkedElu: form.linkedElu, parentMon: form.parentMon,
        drawingReference: form.drawingReference, customerInternalPN: form.customerInternalPN,
        datasheetUrl: form.datasheetUrl, serialNumberLayout: form.serialNumberLayout,
        ninApproval: form.ninApproval, transmittalRef: form.transmittalRef,
        companyCode: (form.companyCode || '').trim(),
        suppliers: Array.isArray(form.suppliers) ? form.suppliers : [],
        notes: form.notes, specs: form.specs.filter(s => s.key || s.value),
      };
      const saved = await saveProduct(getToken, decoded, payload);
      setItem(saved);
      setEditing(false);
      setNameAuto(false);
      if (saved.productNumber !== decoded) {
        navigate(`/product/${encodeURIComponent(saved.productNumber)}`, { replace: true });
      }
    } catch (err) {
      setSaveError(err.message === 'DUPLICATE_PRODUCT_NUMBER'
        ? `A product with number “${nextPn}” already exists.`
        : err.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const Row = ({ label, value }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
      <span style={labelStyle}>{label}</span>
      <span style={{ fontSize: '0.9rem', color: colors.textBody }}>{value || '—'}</span>
    </div>
  );

  const triBool = v => (v === true ? 'Yes' : v === false ? 'No' : '—');

  return (
    <div className="ni-page">
      <div className="ni-shell ni-shell--narrow">
        <header className="ni-header">
          <div className="ni-brand">
            <Link to="/" title="Back to main page" aria-label="Back to main page" style={{ display: 'inline-flex', lineHeight: 0 }}>
              <img src="/north-invent-logo.svg" alt="North Invent" className="ni-brand-logo" />
            </Link>
            <div className="ni-brand-copy">
              <span className="ni-eyebrow">{form.type || 'Product'} · {item.productNumber}</span>
              <span className="ni-brand-subtitle">{item.name || 'Product metadata'}</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Link to="/products" className="ni-button ni-button--ghost" style={{ fontSize: '0.82rem', textDecoration: 'none' }}>← All products</Link>
            {canEdit && !editing && (
              <button className="ni-button ni-button--primary" style={{ fontSize: '0.82rem' }} onClick={() => setEditing(true)}>Edit</button>
            )}
          </div>
        </header>

        <div className="ni-panel" style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {!editing ? (
            <>
              <div style={gridStyle}>
                <Row label="Product number" value={item.productNumber} />
                <Row label="Type" value={item.type} />
                <Row label="Lifecycle status" value={(LIFECYCLE_STATUSES.find(([v]) => v === item.status) || [null, item.status])[1]} />
                <Row label="Name" value={item.name} />
                {canUseChangeControl(user) && <div><span style={labelStyle}>Sample investigations</span><p style={{ margin: '0.35rem 0' }}><Link to={`/quality/sample-investigations?partNumber=${encodeURIComponent(item.productNumber)}`}>Findings across revisions</Link></p><Link className="ni-button" to={`/quality/sample-investigations/new?partNumber=${encodeURIComponent(item.productNumber)}${item.bom?._id ? `&bomId=${encodeURIComponent(item.bom._id)}` : ''}`}>Review a sample</Link></div>}
                {item.bom && (
                  <>
                    <Row label="Linked BOM" value={`${item.bom.documentNumber} — ${item.bom.name}`} />
                    <Row label="Revision (from BOM)" value={`${item.revision || 'not released yet'}${item.bom.currentStatus === 'Superseded' ? ' · BOM superseded — relink' : ''}`} />
                  </>
                )}
                {TEXT_FIELDS.filter(([k]) => fieldVisible(k, item.type)).map(([k, lbl]) => <Row key={k} label={lbl} value={item[k]} />)}
                <Row label="Company" value={`${item.company || '—'} (code ${item.resolvedCompanyCode || '00'}${item.companyCode ? ', overridden' : ', from P/N'})${item.companyWarrantyExtraMonths > 0 ? ` · +${item.companyWarrantyExtraMonths} mo warranty` : ''}`} />
                {fieldVisible('touch', item.type) && <Row label="Touch" value={item.touch === true ? 'Touch' : item.touch === false ? 'Non-touch' : '—'} />}
                {fieldVisible('nvis', item.type) && <Row label="NVIS" value={triBool(item.nvis)} />}
                {fieldVisible('aspectRatio', item.type) && <Row label="Aspect ratio" value={item.aspectRatio} />}
                {fieldVisible('powerSupply', item.type) && <Row label="Power supply" value={item.powerSupply} />}
                {fieldVisible('bonding', item.type) && <Row label="Bonding" value={item.bonding} />}
                {fieldVisible('brightness', item.type) && <Row label="Brightness" value={item.brightness} />}
                {fieldVisible('interfaces', item.type) && <Row label="Interfaces" value={item.interfaces} />}
                {fieldVisible('mounting', item.type) && <Row label="Mounting" value={(item.mounting || []).join(', ')} />}
                {fieldVisible('certifications', item.type) && <Row label="Certifications" value={(item.certifications || []).join(', ')} />}
                {fieldVisible('datasheetConfirmed', item.type) && <Row label="Datasheet confirmed" value={triBool(item.datasheetConfirmed)} />}
                {fieldVisible('biosReady', item.type) && <Row label="BIOS ready" value={triBool(item.biosReady)} />}
              </div>

              <div>
                <span style={labelStyle}>Specifications</span>
                {(item.specs || []).length === 0 ? (
                  <p style={{ color: colors.textMuted, fontSize: '0.88rem', margin: '0.35rem 0 0' }}>No specs recorded.</p>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0.4rem' }}>
                    <tbody>
                      {item.specs.map((s, i) => (
                        <tr key={i} style={{ borderTop: `1px solid ${colors.panelBorder}` }}>
                          <td style={{ padding: '0.35rem 0.5rem 0.35rem 0', color: colors.textMuted, fontSize: '0.85rem', width: '40%' }}>{s.key}</td>
                          <td style={{ padding: '0.35rem 0', color: colors.textBody, fontSize: '0.88rem' }}>{s.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {item.accessories?.length > 0 && (
                <div><span style={labelStyle}>Accessories</span>
                  <div style={{ fontSize: '0.85rem', color: colors.textBody, marginTop: '0.25rem' }}>{item.accessories.join(', ')}</div>
                </div>
              )}
              <div><span style={labelStyle}>Suppliers</span>
                {(item.suppliers || []).length === 0 ? (
                  <p style={{ color: colors.textMuted, fontSize: '0.88rem', margin: '0.35rem 0 0' }}>No suppliers assigned.</p>
                ) : (
                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.35rem' }}>
                    {item.suppliers.map(k => <span key={k} style={supplierChip}>{supplierLabel(k)}</span>)}
                  </div>
                )}
              </div>
              {item.notes && (<div><span style={labelStyle}>Notes</span><p style={{ margin: '0.25rem 0 0', fontSize: '0.88rem', color: colors.textBody, lineHeight: 1.55 }}>{item.notes}</p></div>)}
              {item.quality && <QualityPanel quality={item.quality} />}
              {item.updatedBy && (<div style={{ fontSize: '0.75rem', color: colors.textMuted }}>Last edited by {item.updatedBy}</div>)}
            </>
          ) : (
            <>
              <div style={gridStyle}>
                <label style={fieldWrap}><span style={labelStyle}>Product number</span>
                  <input value={form.productNumber || ''} onChange={e => setField('productNumber', e.target.value)}
                    title="Renaming keeps everything on this record; DIU/ELU/MON links from other products follow the new number" style={inputStyle} />
                </label>
                <label style={fieldWrap}><span style={labelStyle}>Type</span>
                  <select value={form.type || 'MON'} onChange={e => setField('type', e.target.value)} style={inputStyle}>
                    {['MON', 'DIU', 'ELU', 'PAN', 'ACC', 'BOX', 'COMPONENT', 'SOFTWARE'].map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
                <label style={fieldWrap}><span style={labelStyle}>Lifecycle status</span>
                  <select value={form.status || 'production'} onChange={e => setField('status', e.target.value)} style={inputStyle}>
                    {LIFECYCLE_STATUSES.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
                  </select>
                </label>
                <div style={fieldWrap}>
                  <span style={{ ...labelStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.4rem' }}>
                    <span>Name{nameAuto ? ' · auto' : ''}</span>
                    {NAME_GENERATOR_TYPES.includes(form.type) && (
                      <button type="button" className="ni-button ni-button--ghost" style={{ fontSize: '0.66rem', padding: '0 0.4rem', lineHeight: 1.6 }}
                        onClick={applyGeneratedName}
                        title="Generate the name from the structured variables below — it stays editable, and keeps regenerating until you type in it">
                        ⟳ Generate
                      </button>
                    )}
                  </span>
                  <input value={form.name || ''} onChange={e => setField('name', e.target.value)} style={inputStyle} />
                </div>
                {TEXT_FIELDS.filter(([k]) => fieldVisible(k, form.type)).map(([k, lbl]) => (
                  <label key={k} style={fieldWrap}><span style={labelStyle}>{lbl}</span>
                    <input value={form[k] || ''} onChange={e => setField(k, e.target.value)} style={inputStyle} />
                  </label>
                ))}
                <label style={fieldWrap}><span style={labelStyle}>Company code override</span>
                  <input value={form.companyCode || ''} onChange={e => setField('companyCode', e.target.value)} placeholder={`derived: ${item.resolvedCompanyCode || '00'} (${item.company || '—'})`} style={inputStyle} />
                </label>
                {fieldVisible('touch', form.type) && (
                  <label style={fieldWrap}><span style={labelStyle}>Touch</span>
                    <select value={String(form.touch)} onChange={e => setField('touch', e.target.value === 'true' ? true : e.target.value === 'false' ? false : null)} style={inputStyle}>
                      <option value="null">—</option><option value="true">Touch</option><option value="false">Non-touch</option>
                    </select>
                  </label>
                )}
                {fieldVisible('nvis', form.type) && (
                  <label style={fieldWrap}><span style={labelStyle}>NVIS</span>
                    <select value={String(form.nvis ?? null)} onChange={e => setField('nvis', e.target.value === 'true' ? true : e.target.value === 'false' ? false : null)} style={inputStyle}>
                      <option value="null">—</option><option value="true">NVIS</option><option value="false">Non-NVIS</option>
                    </select>
                  </label>
                )}
                {fieldVisible('aspectRatio', form.type) && (
                  <label style={fieldWrap}><span style={labelStyle}>Aspect ratio</span>
                    <select value={form.aspectRatio || ''} onChange={e => setField('aspectRatio', e.target.value)} style={inputStyle}>
                      <option value="">—</option>
                      {ASPECT_RATIO_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </label>
                )}
                {fieldVisible('powerSupply', form.type) && (
                  <label style={fieldWrap}><span style={labelStyle}>Power supply</span>
                    <select value={form.powerSupply || ''} onChange={e => setField('powerSupply', e.target.value)} style={inputStyle}>
                      <option value="">—</option>
                      {['AC', 'DC', 'AC/DC', 'AC/AC', 'DC/DC'].map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </label>
                )}
                {fieldVisible('bonding', form.type) && (
                  <label style={fieldWrap}><span style={labelStyle}>Bonding</span>
                    <select value={form.bonding || ''} onChange={e => setField('bonding', e.target.value)} style={inputStyle}>
                      <option value="">—</option><option value="Air">Air</option><option value="Optical">Optical</option>
                    </select>
                  </label>
                )}
                {fieldVisible('brightness', form.type) && (
                  <label style={fieldWrap}><span style={labelStyle}>Brightness</span>
                    <select value={form.brightness || ''} onChange={e => setField('brightness', e.target.value)} style={inputStyle}>
                      <option value="">—</option><option value="Standard">Standard</option><option value="High bright">High bright</option>
                    </select>
                  </label>
                )}
                {fieldVisible('interfaces', form.type) && (
                  <label style={fieldWrap}><span style={labelStyle}>Interfaces</span>
                    <input value={form.interfaces || ''} onChange={e => setField('interfaces', e.target.value)} placeholder="RS422/485, 2xLAN" style={inputStyle} />
                  </label>
                )}
                {fieldVisible('bomLink', form.type) && (
                  <label style={fieldWrap}><span style={labelStyle}>Linked BOM — drives revision</span>
                    <select value={form.bomId || ''} onChange={e => {
                      const v = e.target.value;
                      const b = boms.find(x => x._id === v);
                      // The record's original link stays selectable even when its
                      // BOM isn't currently Released (mid-revise) — restore from item.
                      const keepOriginal = !b && v && v === item.bomId;
                      setForm(prev => ({
                        ...prev,
                        bomId: b ? b._id : keepOriginal ? item.bomId : '',
                        bomDocumentNumber: b ? b.documentNumber : keepOriginal ? (item.bomDocumentNumber || '') : '',
                        bomName: b ? b.name : keepOriginal ? (item.bomName || '') : '',
                      }));
                    }} style={inputStyle}>
                      <option value="">— none —</option>
                      {item.bomId && !boms.some(b => b._id === item.bomId) && (
                        <option value={item.bomId}>{item.bomDocumentNumber || 'Linked BOM'} — {item.bomName || ''} (not currently released)</option>
                      )}
                      {boms.map(b => <option key={b._id} value={b._id}>{b.documentNumber} — {b.name} (rev {b.currentRevision})</option>)}
                    </select>
                  </label>
                )}
                {fieldVisible('datasheetConfirmed', form.type) && (
                  <label style={fieldWrap}><span style={labelStyle}>Datasheet confirmed</span>
                    <select value={String(!!form.datasheetConfirmed)} onChange={e => setField('datasheetConfirmed', e.target.value === 'true')} style={inputStyle}>
                      <option value="true">Yes</option><option value="false">No</option>
                    </select>
                  </label>
                )}
                {fieldVisible('biosReady', form.type) && (
                  <label style={fieldWrap}><span style={labelStyle}>BIOS ready</span>
                    <select value={String(form.biosReady)} onChange={e => setField('biosReady', e.target.value === 'true' ? true : e.target.value === 'false' ? false : null)} style={inputStyle}>
                      <option value="null">—</option><option value="true">Yes</option><option value="false">No</option>
                    </select>
                  </label>
                )}
                {fieldVisible('certifications', form.type) && (
                  <label style={fieldWrap}><span style={labelStyle}>Certifications / approvals</span>
                    <input value={form.certificationsText || ''} onChange={e => setField('certificationsText', e.target.value)} placeholder="IEC 60945, DNV, MIL-STD-810" style={inputStyle} />
                  </label>
                )}
              </div>

              {fieldVisible('mounting', form.type) && (
                <div>
                  <span style={labelStyle}>Mounting options</span>
                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.4rem' }}>
                    {MOUNTING_OPTIONS.map(opt => {
                      const on = (form.mounting || []).includes(opt);
                      return (
                        <button type="button" key={opt} onClick={() => toggleMounting(opt)} style={supplierToggle(on)}>
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div>
                <span style={labelStyle}>Specifications</span>
                {form.specs.map((s, i) => (
                  <div key={i} style={{ display: 'flex', gap: '0.5rem', marginTop: '0.4rem' }}>
                    <input value={s.key} onChange={e => setSpec(i, 'key', e.target.value)} placeholder="Field" style={{ ...inputStyle, flex: 1 }} />
                    <input value={s.value} onChange={e => setSpec(i, 'value', e.target.value)} placeholder="Value" style={{ ...inputStyle, flex: 2 }} />
                    <button type="button" onClick={() => removeSpec(i)} className="ni-button ni-button--ghost" style={{ padding: '0 0.6rem' }}>×</button>
                  </div>
                ))}
                <button type="button" onClick={addSpec} className="ni-button ni-button--ghost" style={{ marginTop: '0.5rem', fontSize: '0.8rem' }}>+ Add spec</button>
              </div>

              <label style={fieldWrap}><span style={labelStyle}>Notes</span>
                <textarea value={form.notes || ''} onChange={e => setField('notes', e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
              </label>

              <div>
                <span style={labelStyle}>Suppliers (who can see this product)</span>
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.4rem' }}>
                  {SUPPLIERS.map(s => {
                    const on = (form.suppliers || []).includes(s.key);
                    return (
                      <button type="button" key={s.key} onClick={() => toggleSupplier(s.key)} style={supplierToggle(on)}>
                        {s.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {saveError && <p style={{ color: colors.danger, fontSize: '0.85rem', margin: 0 }}>{saveError}</p>}
              <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
                <button className="ni-button ni-button--ghost" onClick={cancel} disabled={saving}>Cancel</button>
                <button className="ni-button ni-button--primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function qColor(n) {
  if (n >= 80) return '#46bb89';
  if (n >= 50) return '#fbbf24';
  return '#ef4444';
}

function QualityPanel({ quality }) {
  const { completeness, correctness, overall, grade, issues } = quality;
  const Bar = ({ label, val }) => (
    <div style={{ flex: 1, minWidth: 160 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: colors.textBody }}>
        <span>{label}</span><span style={{ color: qColor(val), fontWeight: 700 }}>{val}%</span>
      </div>
      <div style={{ height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.08)', marginTop: 3 }}>
        <div style={{ width: `${val}%`, height: '100%', borderRadius: 3, background: qColor(val) }} />
      </div>
    </div>
  );
  return (
    <div style={{ borderTop: `1px solid ${colors.panelBorder}`, paddingTop: '0.85rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.6rem' }}>
        <span style={labelStyle}>Data quality</span>
        <span style={{ fontSize: '0.8rem', fontWeight: 800, color: qColor(overall), border: `1px solid ${qColor(overall)}55`, borderRadius: 6, padding: '0 0.4rem' }}>Grade {grade}</span>
      </div>
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        <Bar label="Completeness" val={completeness} />
        <Bar label="Correctness" val={correctness} />
      </div>
      {issues && issues.length > 0 && (
        <ul style={{ margin: '0.6rem 0 0', paddingLeft: '1.1rem', color: colors.textMuted, fontSize: '0.82rem' }}>
          {issues.map((s, i) => <li key={i}>{s}</li>)}
        </ul>
      )}
    </div>
  );
}

const labelStyle = { fontSize: '0.72rem', fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' };
const supplierChip = {
  display: 'inline-block', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.03em',
  padding: '0.15rem 0.55rem', borderRadius: '999px', color: colors.accent,
  background: 'rgba(70,187,137,0.12)', border: '1px solid rgba(70,187,137,0.3)',
};
const supplierToggle = (on) => ({
  fontSize: '0.78rem', fontWeight: 700, padding: '0.3rem 0.7rem', borderRadius: '999px', cursor: 'pointer',
  border: `1px solid ${on ? colors.accent : colors.panelBorder}`,
  background: on ? 'rgba(70,187,137,0.16)' : 'transparent',
  color: on ? colors.accent : colors.textMuted,
});
const gridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.85rem' };
const fieldWrap = { display: 'flex', flexDirection: 'column', gap: '0.25rem' };
const inputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '0.5rem 0.6rem', fontSize: '0.88rem',
  borderRadius: '8px', border: `1px solid ${colors.panelBorder}`,
  background: 'rgba(255,255,255,0.04)', color: colors.textStrong,
};
